import type { TFunction } from "i18next";

import { getTransportCategory } from "shared/api/api";
import { getFailureMode } from "shared/api/failureModes/api";
import { getIssue } from "shared/api/issues/api";
import { getGroup } from "shared/api/rbac/api";
import { getSmartGroup, getSmartGroupV2 } from "shared/api/smartGroups/api";
import { getVectorCluster } from "shared/api/vectorClusters/api";
import { transformVectorClusterName } from "shared/api/vectorClusters/utils";
import { MAX_FILTER_ID, MIN_FILTER_ID } from "shared/constants";

import { SchemaEntry } from "features/ui/MaterialTable";

import { FilterGroupState } from "./FilterBuilder/types";
import { getTopLevelRowFromFilterGroupState } from "./FilterBuilder/utils";
import { FilterOperator } from "./types";
import { getMinMaxSelectOptions } from "./utils";

const issueFieldNameMapping = ({ id }: { id: string }) =>
  getIssue({ id }).then(({ data }) => data.name);

const failureModeFieldNameMapping = ({ id }: { id: string }) =>
  getFailureMode({ id }).then(({ data }) => data.name);

const transportCategoryFieldNameMapping = ({ id }: { id: string }) =>
  getTransportCategory({ id }).then(({ data }) => data.name);

const groupIDNameMapping = ({ id }: { id: string }) =>
  getGroup({ id }).then(({ data }) => data.name);

// v1 and v2 smart groups coexist. A schema whose filter carries its own
// getValueNameFunc resolves names through that; this shared fallback catches
// the rows that have no schema entry, so try v2 first and fall back to v1.
const smartGroupsNameMapping = ({ id }: { id: string }) =>
  getSmartGroupV2({ id })
    .then(({ data }) => data.name)
    .catch(() => getSmartGroup({ id }).then(({ data }) => data.name));

const similarClusterNameMapping = ({ id }: { id: string }) =>
  getVectorCluster({ id }).then(({ data }) => transformVectorClusterName(data));

const fieldNameMapFunction = {
  failureModeID: failureModeFieldNameMapping,
  transportCategoryID: transportCategoryFieldNameMapping,
  assignedGroupID: groupIDNameMapping,
  promotedToIDs: issueFieldNameMapping,
  smartGroups: smartGroupsNameMapping,
  similarClusterID: similarClusterNameMapping,
};

const resolveValueNames = (
  values: string[],
  resolve: (value: string) => Promise<string>
): Promise<string[]> =>
  Promise.all(
    values.map((value) =>
      value && value !== "null"
        ? resolve(value)
            .then((name) => name)
            // Fall back to the raw value (e.g. an ID) when the lookup fails,
            // rather than rendering the literal string "null".
            .catch(() => value)
        : Promise.resolve(value)
    )
  );

export const transformValues = (
  t: TFunction,
  filters: FilterGroupState,
  fieldName: string,
  tableSchema?: SchemaEntry[]
): Promise<string[]> => {
  const childRow = getTopLevelRowFromFilterGroupState(fieldName, filters);

  if (!childRow) return Promise.resolve([]);

  const { values, operator } = childRow;

  const currentFilterSchema =
    tableSchema?.find(({ accessor }) => accessor === fieldName)?.filter ??
    tableSchema?.find(({ filter }) => filter?.fieldName === fieldName)?.filter;

  const isTextSearchOperator = [
    FilterOperator.CONTAINS,
    FilterOperator.NOT_CONTAINS,
  ].includes(operator);

  // A filter can carry its own value→name resolver (e.g. v2 smart groups),
  // which takes precedence over the shared fieldName-keyed mapping below.
  if (currentFilterSchema?.getValueNameFunc && !isTextSearchOperator) {
    return resolveValueNames(values, currentFilterSchema.getValueNameFunc);
  }

  // Should we do transformation using API
  if (
    Object.keys(fieldNameMapFunction).includes(fieldName) &&
    !isTextSearchOperator
  ) {
    const getDataFunc =
      fieldNameMapFunction[fieldName as keyof typeof fieldNameMapFunction];

    return resolveValueNames(values, (value) => getDataFunc({ id: value }));
  }

  // Should we do transformation using local mappings
  const enableMinMaxFilters = currentFilterSchema?.enableMinMaxFilters;

  if (
    enableMinMaxFilters &&
    [FilterOperator.IN, FilterOperator.NOT_IN].includes(operator) &&
    (values.includes(MAX_FILTER_ID) || values.includes(MIN_FILTER_ID))
  ) {
    const filterType = currentFilterSchema?.filterType;
    const minMaxOptions = getMinMaxSelectOptions(t, filterType);

    const transformedValues = values.map((value) => {
      const transformedValue = minMaxOptions
        ?.find(({ id }) => id === value)
        ?.value?.toString();

      return transformedValue || value;
    });

    return Promise.resolve(transformedValues);
  }

  return Promise.resolve(values);
};
